/**
 * Phase 4.5: Quick Ask Modal
 *
 * Modal component that allows users to ask contextual follow-up questions
 * about a specific MECE tactic without leaving their current view.
 */

import React, { useState } from 'react'
import {
  QuickAskQuestionType,
  QuickAskContext,
  QUESTION_LABELS,
  QUESTION_DESCRIPTIONS,
  buildQuickAskPrompt,
  getQuestionText
} from '@utils/quickAskPromptBuilder'
import './QuickAskModal.css'

interface QuickAskModalProps {
  isOpen: boolean
  onClose: () => void
  tacticId: string
  tacticTitle: string
  tacticDescription: string
  pillarName: string
  supportingEvidence: string[]
  onSubmitQuestion: (prompt: string, questionText: string, questionType: QuickAskQuestionType) => void
  isLoading?: boolean
}

const PRESET_QUESTIONS: QuickAskQuestionType[] = ['why', 'how', 'risks', 'resources', 'timeline']

export const QuickAskModal: React.FC<QuickAskModalProps> = ({
  isOpen,
  onClose,
  tacticId,
  tacticTitle,
  tacticDescription,
  pillarName,
  supportingEvidence,
  onSubmitQuestion,
  isLoading = false
}) => {
  const [customQuestion, setCustomQuestion] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  const handleQuestionSelect = (questionType: QuickAskQuestionType) => {
    if (questionType === 'custom') {
      setShowCustomInput(true)
      return
    }

    const context: QuickAskContext = {
      tactic_id: tacticId,
      tactic_title: tacticTitle,
      tactic_description: tacticDescription,
      pillar_name: pillarName,
      supporting_evidence: supportingEvidence,
      question_type: questionType
    }

    const prompt = buildQuickAskPrompt(context)
    const questionText = getQuestionText(questionType, tacticTitle)
    onSubmitQuestion(prompt, questionText, questionType)
  }

  const handleCustomSubmit = () => {
    if (!customQuestion.trim()) return

    const context: QuickAskContext = {
      tactic_id: tacticId,
      tactic_title: tacticTitle,
      tactic_description: tacticDescription,
      pillar_name: pillarName,
      supporting_evidence: supportingEvidence,
      question_type: 'custom',
      custom_question: customQuestion
    }

    const prompt = buildQuickAskPrompt(context)
    onSubmitQuestion(prompt, customQuestion, 'custom')
    setCustomQuestion('')
    setShowCustomInput(false)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="quick-ask-backdrop" onClick={handleBackdropClick}>
      <div className="quick-ask-modal">
        <div className="quick-ask-header">
          <h3>Quick Ask</h3>
          <button className="close-btn" onClick={onClose} disabled={isLoading}>
            &times;
          </button>
        </div>

        <div className="quick-ask-context">
          <div className="context-label">About:</div>
          <div className="context-tactic">{tacticTitle}</div>
          <div className="context-pillar">{pillarName}</div>
        </div>

        {isLoading ? (
          <div className="quick-ask-loading">
            <div className="loading-spinner"></div>
            <p className="loading-message">Analyzing your question...</p>
            <p className="loading-hint">This may take up to a minute while we search the knowledge graph.</p>
          </div>
        ) : showCustomInput ? (
          <div className="custom-question-section">
            <label>Your question:</label>
            <textarea
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              placeholder="Type your question about this recommendation..."
              rows={3}
              autoFocus
            />
            <div className="custom-actions">
              <button
                className="btn-back"
                onClick={() => {
                  setShowCustomInput(false)
                  setCustomQuestion('')
                }}
              >
                Back
              </button>
              <button
                className="btn-submit"
                onClick={handleCustomSubmit}
                disabled={!customQuestion.trim()}
              >
                Ask Question
              </button>
            </div>
          </div>
        ) : (
          <div className="question-options">
            <p className="options-prompt">What would you like to know?</p>
            <div className="options-grid">
              {PRESET_QUESTIONS.map((qType) => (
                <button
                  key={qType}
                  className="question-btn"
                  onClick={() => handleQuestionSelect(qType)}
                  title={QUESTION_DESCRIPTIONS[qType]}
                >
                  <span className="question-icon">{getQuestionIcon(qType)}</span>
                  <span className="question-label">{QUESTION_LABELS[qType]}</span>
                </button>
              ))}
              <button
                className="question-btn custom-btn"
                onClick={() => setShowCustomInput(true)}
                title={QUESTION_DESCRIPTIONS.custom}
              >
                <span className="question-icon">...</span>
                <span className="question-label">{QUESTION_LABELS.custom}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function getQuestionIcon(questionType: QuickAskQuestionType): string {
  const icons: Record<QuickAskQuestionType, string> = {
    why: '?',
    how: '#',
    risks: '!',
    resources: '$',
    timeline: '@',
    custom: '...'
  }
  return icons[questionType]
}

export default QuickAskModal
